/**
 * RNNoise Provider
 *
 * Noise suppression using RNNoise WASM module.
 * Based on Mozilla/Xiph RNNoise, Apache 2.0 license.
 *
 * RNNoise operates at 48kHz sample rate with 480 sample frames (10ms).
 * If input is at different sample rate, we resample before processing.
 */

import { NoiseSuppressionProvider } from './NoiseSuppressionProvider.js';
import { createRNNWasmModule } from '@jitsi/rnnoise-wasm';

// RNNoise constants
const RNNOISE_SAMPLE_RATE = 48000;
const RNNOISE_FRAME_SIZE = 480; // 10ms at 48kHz

export class RNNoiseProvider extends NoiseSuppressionProvider {
  constructor() {
    super('rnnoise');
    this.module = null;
    this.state = null;
    this.inputPtr = null;
    this.outputPtr = null;
    this.initialized = false;

    // Resampling buffers (for non-48kHz input)
    this.resampleBuffer = null;
    this.outputBuffer = null;
    this.leftoverSamples = null;
  }

  /**
   * Check if RNNoise is supported on this device
   * Requires WASM SIMD for optimal performance
   */
  static isSupported(capabilities) {
    return capabilities.wasmSimd;
  }

  /**
   * Initialize RNNoise WASM module
   */
  async initialize() {
    if (this.initialized) {
      return;
    }

    console.log('[RNNoise] Initializing...');

    try {
      // Load the WASM module
      this.module = await createRNNWasmModule({
        locateFile: (path) => {
          // Use the WASM file from public directory
          return `/wasm/${path}`;
        }
      });

      // Wait for module to be ready
      await this.module.ready;

      // Initialize RNNoise
      this.module._rnnoise_init();

      // Create denoiser state
      this.state = this.module._rnnoise_create();

      // Allocate memory for input/output buffers (480 floats = 1920 bytes)
      this.inputPtr = this.module._malloc(RNNOISE_FRAME_SIZE * 4);
      this.outputPtr = this.module._malloc(RNNOISE_FRAME_SIZE * 4);

      // Initialize resampling buffers
      this.leftoverSamples = new Float32Array(0);

      this.initialized = true;
      console.log('[RNNoise] Initialized successfully');
    } catch (error) {
      console.error('[RNNoise] Initialization failed:', error);
      throw error;
    }
  }

  /**
   * Process audio samples
   * Handles resampling if needed
   *
   * @param {Float32Array} samples - Input audio samples
   * @param {number} sampleRate - Input sample rate
   * @returns {Float32Array} - Processed audio samples
   */
  process(samples, sampleRate) {
    if (!this.initialized) {
      console.warn('[RNNoise] Not initialized, returning original samples');
      return samples;
    }

    // If sample rate matches RNNoise, process directly
    if (sampleRate === RNNOISE_SAMPLE_RATE) {
      return this.processFrames(samples);
    }

    // Need to resample
    return this.processWithResampling(samples, sampleRate);
  }

  /**
   * Process audio frames at 48kHz
   * @param {Float32Array} samples - Audio samples at 48kHz
   * @returns {Float32Array} - Processed samples
   */
  processFrames(samples) {
    // Combine with leftover samples from previous call
    let inputSamples;
    if (this.leftoverSamples.length > 0) {
      inputSamples = new Float32Array(this.leftoverSamples.length + samples.length);
      inputSamples.set(this.leftoverSamples);
      inputSamples.set(samples, this.leftoverSamples.length);
    } else {
      inputSamples = samples;
    }

    // Calculate number of complete frames
    const numFrames = Math.floor(inputSamples.length / RNNOISE_FRAME_SIZE);
    const processedLength = numFrames * RNNOISE_FRAME_SIZE;

    // Store leftover samples for next call
    if (inputSamples.length > processedLength) {
      this.leftoverSamples = inputSamples.slice(processedLength);
    } else {
      this.leftoverSamples = new Float32Array(0);
    }

    if (numFrames === 0) {
      return new Float32Array(0);
    }

    // Process all complete frames
    const output = new Float32Array(processedLength);

    for (let i = 0; i < numFrames; i++) {
      const frameStart = i * RNNOISE_FRAME_SIZE;
      const frameInput = inputSamples.subarray(frameStart, frameStart + RNNOISE_FRAME_SIZE);

      // Convert to float32 values RNNoise expects (scaled)
      // RNNoise expects values in range approximately -32768 to 32767
      const scaledInput = new Float32Array(RNNOISE_FRAME_SIZE);
      for (let j = 0; j < RNNOISE_FRAME_SIZE; j++) {
        scaledInput[j] = frameInput[j] * 32768;
      }

      // Copy input to WASM memory
      this.module.HEAPF32.set(scaledInput, this.inputPtr / 4);

      // Process frame
      this.module._rnnoise_process_frame(this.state, this.outputPtr, this.inputPtr);

      // Copy output from WASM memory
      const processedFrame = this.module.HEAPF32.subarray(
        this.outputPtr / 4,
        this.outputPtr / 4 + RNNOISE_FRAME_SIZE
      );

      // Scale back to normalized range (-1 to 1)
      for (let j = 0; j < RNNOISE_FRAME_SIZE; j++) {
        output[frameStart + j] = processedFrame[j] / 32768;
      }
    }

    return output;
  }

  /**
   * Process audio with resampling from different sample rate
   * @param {Float32Array} samples - Audio samples
   * @param {number} sampleRate - Input sample rate
   * @returns {Float32Array} - Processed samples at original sample rate
   */
  processWithResampling(samples, sampleRate) {
    // Simple linear resampling (for production, use a proper resampler)
    const ratio = RNNOISE_SAMPLE_RATE / sampleRate;
    const resampledLength = Math.floor(samples.length * ratio);
    const resampled = new Float32Array(resampledLength);

    for (let i = 0; i < resampledLength; i++) {
      const srcIndex = i / ratio;
      const srcFloor = Math.floor(srcIndex);
      const srcCeil = Math.min(srcFloor + 1, samples.length - 1);
      const t = srcIndex - srcFloor;
      resampled[i] = samples[srcFloor] * (1 - t) + samples[srcCeil] * t;
    }

    // Process at 48kHz
    const processed = this.processFrames(resampled);

    // Resample back to original rate
    const outputRatio = sampleRate / RNNOISE_SAMPLE_RATE;
    const outputLength = Math.floor(processed.length * outputRatio);
    const output = new Float32Array(outputLength);

    for (let i = 0; i < outputLength; i++) {
      const srcIndex = i / outputRatio;
      const srcFloor = Math.floor(srcIndex);
      const srcCeil = Math.min(srcFloor + 1, processed.length - 1);
      const t = srcIndex - srcFloor;
      output[i] = processed[srcFloor] * (1 - t) + processed[srcCeil] * t;
    }

    return output;
  }

  /**
   * Clean up resources
   */
  async destroy() {
    if (!this.initialized) {
      return;
    }

    console.log('[RNNoise] Destroying...');

    try {
      if (this.inputPtr) {
        this.module._free(this.inputPtr);
        this.inputPtr = null;
      }

      if (this.outputPtr) {
        this.module._free(this.outputPtr);
        this.outputPtr = null;
      }

      if (this.state) {
        this.module._rnnoise_destroy(this.state);
        this.state = null;
      }

      this.module = null;
      this.initialized = false;
      this.leftoverSamples = null;

      console.log('[RNNoise] Destroyed successfully');
    } catch (error) {
      console.error('[RNNoise] Destroy failed:', error);
    }
  }
}

export default RNNoiseProvider;
